import { createSlice } from "@reduxjs/toolkit";
import {
  autoLogin,
  contractLogin,
  logout,
} from "./thunks/authenticationThunks";
import {
  contractLogin2fa,
  contractLoginRecoveryCode,
} from "./thunks/twoFactorThunks";
import { changeContractPassword } from "./thunks/accountThunks";
import {
  createAuthenticatedState,
  createRecoveryCodeAuthenticatedState,
  createFailedTwoFactorState,
  createTwoFactorPendingState,
  initialState,
  isCurrentTwoFactorRequest,
} from "./state/loginState";

export const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    cancelTwoFactor: () => ({
      ...initialState,
    }),
    consumeRecoveryCodeRecommendation: (state) => ({
      ...state,
      loggedInWithRecoveryCode: false,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(logout.fulfilled, () => ({
      ...initialState,
    }));

    builder.addCase(contractLogin.pending, (state) => ({
      ...state,
      loggedIn: false,
      accessToken: null,
      contractId: null,
      companyName: null,
      requiresPasswordChange: null,
      pendingAccessToken: null,
      requiresTwoFactor: false,
      twoFactorRequestId: null,
      loggedInWithRecoveryCode: false,
      loggingIn: true,
      loginStatus: "loading",
    }));
    builder.addCase(contractLogin.rejected, () => ({
      ...initialState,
      loginStatus: "failed",
    }));
    builder.addCase(contractLogin.fulfilled, (state, action) => {
      if (action.payload.required2fa === true) {
        return createTwoFactorPendingState(state, action.payload);
      }
      return createAuthenticatedState(state, action.payload);
    });

    builder.addCase(contractLogin2fa.pending, (state, action) => ({
      ...state,
      loggedInWithRecoveryCode: false,
      twoFactorRequestId: action.meta.requestId,
      loggingIn: true,
      loginStatus: "loading",
    }));
    builder.addCase(contractLogin2fa.rejected, (state, action) => {
      if (!isCurrentTwoFactorRequest(state, action.meta.requestId)) {
        return state;
      }
      return createFailedTwoFactorState(state);
    });
    builder.addCase(contractLogin2fa.fulfilled, (state, action) => {
      if (!isCurrentTwoFactorRequest(state, action.meta.requestId)) {
        return state;
      }
      return createAuthenticatedState(state, action.payload);
    });

    builder.addCase(contractLoginRecoveryCode.pending, (state, action) => ({
      ...state,
      loggedInWithRecoveryCode: false,
      twoFactorRequestId: action.meta.requestId,
      loggingIn: true,
      loginStatus: "loading",
    }));
    builder.addCase(contractLoginRecoveryCode.rejected, (state, action) => {
      if (!isCurrentTwoFactorRequest(state, action.meta.requestId)) {
        return state;
      }
      return createFailedTwoFactorState(state);
    });
    builder.addCase(
      contractLoginRecoveryCode.fulfilled,
      (state, action) => {
        if (!isCurrentTwoFactorRequest(state, action.meta.requestId)) {
          return state;
        }
        return createRecoveryCodeAuthenticatedState(state, action.payload);
      }
    );

    builder.addCase(changeContractPassword.fulfilled, (state) => ({
      ...state,
      requiresPasswordChange: false,
    }));

    builder.addCase(autoLogin.pending, (state) => ({
      ...state,
      loggedInWithRecoveryCode: false,
      twoFactorRequestId: null,
      loggingIn: true,
      loginStatus: "loading",
    }));
    builder.addCase(autoLogin.rejected, () => ({
      ...initialState,
    }));
    builder.addCase(autoLogin.fulfilled, (state, action) =>
      createAuthenticatedState(state, action.payload)
    );
  },
});

export const {
  cancelTwoFactor,
  consumeRecoveryCodeRecommendation,
} = loginSlice.actions;

export default loginSlice.reducer;

export * from "./state/loginState";
export * from "./thunks/authenticationThunks";
export * from "./thunks/twoFactorThunks";
export * from "./thunks/accountThunks";
export {
  ACCESS_TOKEN_STORAGE_KEY,
  clearStoredAccessToken,
  persistAccessToken,
  readStoredAccessToken,
} from "./services/tokenStorage";
